import { fetchProjectById, Project } from '../../../store/Project';
import { RelatedInventoryRiskItem } from '../../../Api/Projects/projectsApi';
import { SingleAssetRiskWidget } from '../../Compounds/RiskSummaryWidget';
import { StatusUpdateSection } from '../../Compounds/StatusUpdate';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { AttachmentTable } from '../../Compounds/AttachmentTable';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { Text } from '../../Atoms/Text';
import { Tags } from '../../Compounds/Tagging';
import { SupportedTagEntities, SupportedTagEntityTypes } from '../../../store/Tags';
import { inventoryRowAccountId } from '../Inventory/inventoryGridColumnDefs';
import { shouldSeeComments } from '../../../store/User';
import { ProjectAssetContacts } from './ProjectAssetContacts';
import { AssetDetailRows, AssetHistoryCard } from './AssetDetailSections';
import { useProjectAssetDetails } from './useProjectAssetDetails';
import { ProjectCustomFieldsSection } from './ProjectCustomFieldsSection';

export interface ProjectSingleAssetViewProps {
  project: Project;
  item: RelatedInventoryRiskItem;
  // What the Tags widget writes to. The project when the page stands for a whole one-asset project,
  // the asset when the user opened this individual record from the Projects page's Inventory mode —
  // there the grid's Tags column shows the asset's own tags, and the widget must match the row.
  tagEntity?: 'project' | 'asset';
}

// Full-screen 1 Project : 1 Asset layout: the single related item's detail sections
// laid out on the page (no drawer), with the risk status and Next Steps up top.
export const ProjectSingleAssetView = ({ project, item, tagEntity = 'project' }: ProjectSingleAssetViewProps) => {
  // The item arrives fully populated in the detail response, so no inventory-store load is pending.
  const { fieldList, account } = useProjectAssetDetails(item, project.account?.id);
  // Either way a tag write is re-read from the project: the asset's tags arrive on its relatedInventory
  // row. Asking for that payload back is what keeps this view rendered — without it the re-read would
  // drop the asset the page is addressing and collapse to the grouped project layout.
  const refetchProject = () => fetchProjectById(project.id, true);
  const tagTarget =
    tagEntity === 'asset'
      ? {
          entity: item as SupportedTagEntities,
          entityId: item.id,
          // Tag ownership keys on the portal account id; a synced asset's account_id is Salesforce's,
          // which would offer the picker no tags and create new ones against an unbindable account.
          entityAccountId: inventoryRowAccountId(item),
          entityType: SupportedTagEntityTypes.ASSET
        }
      : {
          entity: project,
          entityId: project.id,
          entityAccountId: project.account?.id,
          entityType: SupportedTagEntityTypes.PROJECT
        };

  return (
    <div
      className="flex flex-col gap-4 mt-6"
      data-testid="project-single-asset-view"
      data-cy="project-single-asset-view">
      <div className="flex items-stretch gap-4">
        <SingleAssetRiskWidget
          item={item}
          className="w-1/2">
          <StatusUpdateSection entityId={project.id} />
        </SingleAssetRiskWidget>
        <DetailViewCard
          enableCollapse
          key="single-asset-details"
          title="Details"
          className="w-1/2 !my-0"
          collapsed={false}>
          <div className="overflow-auto max-h-[44vh]">
            <AssetDetailRows
              fields={fieldList}
              loading={false}
              wrapperClass="grid grid-cols-1 2xl:grid-cols-2 gap-x-8"
            />
            <div
              className="flex"
              data-testid="single-asset-tags">
              <Text
                className="font-sans text-grey-4 w-full"
                size="sm14">
                Tags
              </Text>
              <div className="w-full">
                <Tags
                  {...tagTarget}
                  entityFetchFn={refetchProject}
                  showCTAButton={true}
                />
              </div>
            </div>
            <ProjectCustomFieldsSection
              project={project}
              asset={item}
              className="mt-4 pt-4 border-t border-gray-200"
            />
            <ProjectCustomFieldsSection
              project={project}
              heading="Project Custom Fields"
              className="mt-4 pt-4 border-t border-gray-200"
            />
          </div>
        </DetailViewCard>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-1/2 flex flex-col gap-4">
          <AttachmentTable
            id={item.id}
            parentType="Asset"
            className="!mb-[14px]"
          />
          <ProjectAssetContacts
            assetId={item.id}
            projectId={item.projectId || project.id}
            accountId={account?.id || ''}
            className="!mt-[14px]"
          />
        </div>
        <div className="w-1/2 flex flex-col gap-4">
          {shouldSeeComments() && item.id && (
            <DetailsComments
              id={item.id}
              entityType="Inventory"
              entityAccountId={item.account_id || ''}
              className="!mb-[14px]"
            />
          )}
          <AssetHistoryCard
            notes={item.notes}
            collapsed={false}
            bodyMaxHeightClass="max-h-[40vh]"
            className="!mt-[14px]"
          />
        </div>
      </div>
    </div>
  );
};
